import type { Metadata } from '#dn-types/api.ts';
import * as Cookies from '#util/cookies.ts';
import {
  AuthenticationError,
  AuthorizationError,
  FetchError,
  InternalServerError,
  MissingResourceError,
  NetworkError,
  RateLimitError,
  TimeoutError,
  ValidationError,
  type AuthErrorCode,
  type ErrorDetails,
} from './errors.ts';
import { readNDJSONStream } from './ndjson.ts';

export const HOST = import.meta.env.VITE_API_URL;

type ErrorBody = { errors: ErrorDetails[] };

/**
 * Pulls our auth token out of its cookie and add some default headers
 */
function buildHeaders(overrides?: HeadersInit) {
  const headers = new Headers(overrides);

  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }

  // Only set authorization header if one was not set in overrides
  if (!headers.has('Authorization')) {
    const userToken = Cookies.getItem(Cookies.USER_TOKEN_NAME);
    const authToken = Cookies.getItem(Cookies.AUTH_TOKEN_NAME);
    const oidcToken = Cookies.getItem(Cookies.OIDC_TOKEN_NAME);
    if (userToken) {
      headers.set('Authorization', `Bearer ${userToken} ${authToken}`.trim());
    } else if (oidcToken) {
      headers.set('Authorization', `Bearer ${oidcToken}`);
    }
  }

  // Filter out falsy values
  for (const [key, value] of headers.entries()) {
    if (!value) {
      headers.delete(key);
    }
  }

  return headers;
}

/**
 * `fetch`, but a transport-level failure becomes a {@link NetworkError} instead of the bare `TypeError`
 * the Fetch spec provides, which carries no status, no body, and a browser-specific message that never
 * says the request failed to leave the machine. An aborted request rejects with a `DOMException`
 * rather than a `TypeError`, so cancellation still passes through untouched.
 *
 * This only covers the fetch call itself (connect/headers phase). A connection
 * dropped mid-body rejects the response read (`response.json()`, `.blob()`,
 * `reader.read()`) with the same bare TypeError, so body reads go through
 * {@link convertBodyReadError}/`readBody` below.
 */
async function apiFetch(url: string, init: RequestInit) {
  try {
    return await fetch(url, init);
  } catch (error) {
    if (error instanceof TypeError) {
      throw new NetworkError(url, { cause: error });
    }
    throw error;
  }
}

/**
 * Convert a body-read failure (connection dropped mid-response) into a
 * {@link NetworkError}, so mid-body transport failures group with the
 * connect/headers-phase ones from {@link apiFetch} instead of fragmenting
 * Sentry on the browser-specific TypeError message. Used by {@link readBody}
 * and {@link getNDJSONStream}.
 */
function convertBodyReadError(url: string) {
  return (error: unknown): never => {
    if (error instanceof TypeError) {
      throw new NetworkError(url, { cause: error });
    }
    throw error;
  };
}

/**
 * Read an `ok` response body, converting a mid-response transport failure the
 * same way {@link apiFetch} converts the connect/headers phase.
 *
 * `url` should be the absolute request URL, matching what {@link apiFetch} records, so a
 * given endpoint reports the same `NetworkError.url` whichever phase dropped. `response.url`
 * is not a substitute: it is empty on a mocked or opaque response.
 */
function readBody<T>(url: string, read: () => Promise<T>): Promise<T> {
  return read().catch(convertBodyReadError(url));
}

/**
 * convertErrors takes an api response that is not `ok`, and attempts to convert it
 * into a more specific error type which can be handled by our application code.
 *
 * Most branches below already fall back to a status-derived error when the body will not
 * parse, so a connection dropped while reading one still surfaces as a typed `FetchError`.
 * The branches that need the parsed body to build their error read it through
 * {@link readBody}, so a transport failure there becomes a {@link NetworkError} rather
 * than the bare TypeError that reaches the error boundary anonymously.
 *
 * `requestUrl` is the absolute URL the request went to. It defaults to `response.url` for
 * external callers, which is accurate for a real response and empty for a synthesized one.
 */
export async function convertErrors(response: Response, requestUrl = response.url): Promise<never> {
  // Clone the response before we consume the json, so devtools can show it too
  const clonedResponse = response.clone();

  // 400 validation error — parse the body so the specific field/message reaches our error tracking.
  // GET requests funnel through here (unlike mutations, which parse 400s in modifyRemoteData), so
  // without this branch a GET 400 fell through to the catch-all FetchError below and lost its message.
  if (response.status === 400) {
    const body = (await clonedResponse.json().catch(() => null)) as ErrorBody | null;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (body?.errors?.length) {
      throw new ValidationError(body.errors, response);
    }
    throw new FetchError(response);
  }

  // Authentication error
  if (response.status === 401) {
    const body = (await clonedResponse.json().catch(() => {
      throw new FetchError(response);
    })) as ErrorBody;
    const error = body.errors[0];
    if (
      error?.code === 'ERR_UNAUTHORIZED' ||
      error?.code === 'ERR_2FA_REQUIRED' ||
      error?.code === 'ERR_2FA_RENEWAL_REQUIRED'
    ) {
      throw new AuthenticationError(error as ErrorDetails<AuthErrorCode>, response);
    }
    throw new FetchError(response);
  }

  // 403 Authorization error
  if (response.status === 403) {
    throw new AuthorizationError(response);
  }

  // 404 not found error
  if (response.status === 404) {
    throw new MissingResourceError(response);
  }

  // 408 timeout error
  if (response.status === 408) {
    const body = await readBody(requestUrl, () => clonedResponse.json() as Promise<ErrorBody>);
    throw new TimeoutError(body.errors, response);
  }

  // 429 rate limit error
  if (response.status === 429) {
    throw new RateLimitError(response);
  }

  // 500 error
  if (response.status === 500) {
    const body = (await clonedResponse.json().catch(() => {
      throw new InternalServerError([], response);
    })) as ErrorBody;
    throw new InternalServerError(body.errors, response);
  }

  // Catch-all error
  throw new FetchError(response);
}

/**
 * Helper function to send POST requests against our api.
 */
export async function post<TReturn = unknown>(
  url: `/v${number}/${string}`,
  data = {},
  fetchInit?: RequestInit
): Promise<TReturn> {
  return modifyRemoteData('POST', url, data, fetchInit);
}

/**
 * Helper function to send PUT requests against our api.
 */
export async function put<TReturn = unknown>(
  url: `/v${number}/${string}`,
  data = {},
  fetchInit?: RequestInit
): Promise<TReturn> {
  return modifyRemoteData('PUT', url, data, fetchInit);
}

/**
 * Helper function to send PATCH requests against our api.
 */
export async function patch<TReturn = unknown>(
  url: `/v${number}/${string}`,
  data = {},
  fetchInit?: RequestInit
): Promise<TReturn> {
  return modifyRemoteData('PATCH', url, data, fetchInit);
}

/**
 * Helper function to send DELETE requests against our api.
 */
export async function del<TReturn = unknown>(
  url: `/v${number}/${string}`,
  data = {},
  fetchInit?: RequestInit
): Promise<TReturn> {
  return modifyRemoteData('DELETE', url, data, fetchInit);
}

/**
 * Helper function to send GET requests against our api.
 */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export async function get<TReturn = unknown>(
  url: `/v${number}/${string}`,
  fetchInit: RequestInit = {}
): Promise<{ data: TReturn }> {
  const { headers: initHeaders, ...init } = fetchInit;
  const headers = buildHeaders(initHeaders);

  const requestUrl = `${HOST}${url}`;
  const response = await apiFetch(requestUrl, {
    method: 'GET',
    headers,
    redirect: 'follow',
    referrerPolicy: 'no-referrer',
    ...init,
  });

  if (response.ok) {
    return readBody(requestUrl, () => response.json() as Promise<{ data: TReturn }>);
  }

  return await convertErrors(response, requestUrl);
}

/**
 * Helper function to send GET requests against our api to retrieve a list of items, including metadata.
 */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export async function getList<TReturn = unknown, TMetadata = Metadata>(
  url: `/v${number}/${string}`,
  fetchInit: RequestInit = {}
): Promise<{ data: TReturn; metadata: TMetadata }> {
  const { headers: initHeaders, ...init } = fetchInit;
  const headers = buildHeaders(initHeaders);

  const requestUrl = `${HOST}${url}`;
  const response = await apiFetch(requestUrl, {
    method: 'GET',
    headers,
    redirect: 'follow',
    referrerPolicy: 'no-referrer',
    ...init,
  });

  if (response.ok) {
    return readBody(requestUrl, () => response.json() as Promise<{ data: TReturn; metadata: TMetadata }>);
  }

  return await convertErrors(response, requestUrl);
}

/**
 * Helper function to send GET requests against our api, and return the value as a blob.
 * For example, we use this to generate a Blob for downloading CSV files from the audit log.
 */
export async function getBlob(url: `/v${number}/${string}`, fetchInit: RequestInit = {}): Promise<Blob> {
  const { headers: initHeaders, ...init } = fetchInit;
  const headers = buildHeaders(initHeaders);

  const requestUrl = `${HOST}${url}`;
  const response = await apiFetch(requestUrl, {
    method: 'GET',
    headers,
    redirect: 'follow',
    referrerPolicy: 'no-referrer',
    ...init,
  });

  if (response.ok) {
    return readBody(requestUrl, () => response.blob());
  }

  return await convertErrors(response, requestUrl);
}

/**
 * This is an async generator function which yields lines of JSON as they are streamed from
 * an endpoint sending an NDJSON formatted response.
 */
export async function* getNDJSONStream(
  url: `/v${number}/${string}`,
  data = {},
  streamErrorHandler?: (error: unknown, line: string) => void,
  fetchInit: RequestInit = {}
) {
  const { headers: initHeaders, ...init } = fetchInit;
  const headers = buildHeaders(initHeaders);

  const requestUrl = `${import.meta.env.VITE_API_URL}${url}`;
  const response = await apiFetch(requestUrl, {
    method: 'POST',
    headers,
    redirect: 'follow',
    referrerPolicy: 'no-referrer',
    body: JSON.stringify(data),
    ...init,
  });

  if (response.ok && response.body) {
    // A connection dropped mid-stream rejects `reader.read()` with the same bare
    // TypeError the headers phase throws; convert it the same way so it groups
    // with the other NetworkErrors instead of fragmenting Sentry.
    try {
      yield* readNDJSONStream(response.body, streamErrorHandler);
    } catch (error) {
      convertBodyReadError(requestUrl)(error);
    }
    return;
  }

  // Form validation errors
  if (response.status === 400) {
    // Clone the response before we consume the json, so devtools can show it too
    const clonedResponse = response.clone();
    const body = await readBody(requestUrl, () => clonedResponse.json() as Promise<ErrorBody>);
    // Lazy type assertion here, would be good to do better, but I couldn't figure it out yet
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!body.errors?.length) {
      // If this happens, we are in a situation we didn't expect
      throw new Error('No errors in validation error');
    }

    throw new ValidationError(body.errors, response);
  }

  return await convertErrors(response, requestUrl);
}

/**
 * The way we handle POST, PUT, PATCH, and DELETE requests are very similar, so this DRYs them up.
 */
async function modifyRemoteData<TReturn = unknown>(
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  url: `/v${number}/${string}`,
  data = {},
  fetchInit: RequestInit = {}
): Promise<TReturn> {
  const { headers: initHeaders, ...init } = fetchInit;
  const headers = buildHeaders(initHeaders);

  const requestUrl = `${HOST}${url}`;
  const response = await apiFetch(requestUrl, {
    method,
    headers,
    redirect: 'follow',
    referrerPolicy: 'no-referrer',
    body: JSON.stringify(data),
    ...init,
  });

  if (response.ok) {
    return readBody(requestUrl, () => response.json() as Promise<{ data: TReturn }>).then(
      ({ data: responseData }) => responseData
    );
  }

  // Form validation errors
  if (response.status === 400) {
    // Clone the response before we consume the json, so devtools can show it too
    const clonedResponse = response.clone();
    const body = await readBody(requestUrl, () => clonedResponse.json() as Promise<ErrorBody>);
    // Lazy type assertion here, would be good to do better, but I couldn't figure it out yet
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!body.errors?.length) {
      // If this happens, we are in a situation we didn't expect
      throw new Error('No errors in validation error');
    }

    throw new ValidationError(body.errors, response);
  }

  return await convertErrors(response, requestUrl);
}
